import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ExternalLink, FileText, Trash2, Search, CheckCircle2, Plus, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import PdfPreviewDialog from "@/components/PdfPreviewDialog";

export default function FacturasPage() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [pdfPath, setPdfPath] = useState(null);
  const [pdfName, setPdfName] = useState("");
  const [search, setSearch] = useState("");
  const [onlyPending, setOnlyPending] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [i, c, v] = await Promise.all([api.get("/invoices"), api.get("/clients"), api.get("/vehicles")]);
      setItems(i.data); setClients(c.data); setVehicles(v.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!confirm("¿Borrar factura? El presupuesto vinculado se desvinculará.")) return;
    try { await api.delete(`/invoices/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const cn = (id) => clients.find(c => c.id === id)?.name || "—";
  const vn = (id) => { const v = vehicles.find(x => x.id === id); return v ? v.plate : "—"; };

  const norm = (s) => (s || "").toString().toLowerCase();
  const filtered = items.filter((it) => {
    if (onlyPending && it.paid) return false;
    if (!search.trim()) return true;
    const n = norm(search);
    return [it.number, cn(it.client_id), vn(it.vehicle_id), (it.date || "").slice(0,10), it.paid ? "cobrada" : "pendiente"]
      .some((f) => norm(f).includes(n));
  });

  return (
    <div data-testid="facturas-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="section-title">Facturas</h1>
          <p className="text-sm text-zinc-500 mt-1">Facturas dinámicas: añade las líneas que necesites, o conviértelas desde un presupuesto.</p>
        </div>
        <Button
          onClick={async () => {
            try {
              const today = new Date().toISOString().slice(0, 10) + "T12:00:00+00:00";
              const r = await api.post("/invoices", { date: today, lines: [], vat_rate: 21, iban: "", notes: "" });
              toast.success(`Factura ${r.data.number} creada`);
              nav(`/facturas/${r.data.id}`);
            } catch (e) { toast.error(formatApiError(e)); }
          }}
          data-testid="btn-new-invoice"
        >
          <Plus size={14} className="mr-1" /> Nueva factura
        </Button>
      </div>

      <div className="mb-4 flex items-center gap-2 max-w-lg">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            data-testid="invoices-search"
            placeholder="Buscar por número, cliente, matrícula o estado…"
            className="pl-8"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button
          data-testid="btn-filter-pending"
          onClick={() => setOnlyPending((v) => !v)}
          className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border transition-colors whitespace-nowrap ${
            onlyPending
              ? "bg-zinc-900 text-white border-zinc-900"
              : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
          }`}
        >
          <Clock size={13} />
          Pendientes
        </button>
        <span className="text-xs text-zinc-400">{filtered.length} / {items.length}</span>
      </div>

      <div className="page-card overflow-x-auto">
        <table className="tbl" data-testid="invoices-table">
          <thead><tr><th>Nº</th><th>Fecha</th><th>Cliente</th><th>Matrícula</th><th>Estado</th><th>Total</th><th></th></tr></thead>
          <tbody>
            {filtered.map((inv) => {
              const subtotal = (inv.lines || []).reduce((s, l) => s + Number(l.amount || 0), 0);
              const total = subtotal * (1 + Number(inv.vat_rate || 0) / 100);
              return (
                <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                  <td className="font-mono">{inv.number}</td>
                  <td>{(inv.date || "").slice(0, 10)}</td>
                  <td>{cn(inv.client_id)}</td>
                  <td className="font-mono">{vn(inv.vehicle_id)}</td>
                  <td>
                    {inv.paid
                      ? <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700"><CheckCircle2 size={12} /> Cobrada</span>
                      : <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-700">Pendiente</span>
                    }
                  </td>
                  <td>{total.toFixed(2)} €</td>
                  <td className="text-right whitespace-nowrap">
                    <Link to={`/facturas/${inv.id}`} className="inline-block px-2 py-1 hover:bg-zinc-100 rounded"><ExternalLink size={14} /></Link>
                    <button onClick={() => { setPdfName(`factura_${inv.number}.pdf`); setPdfPath(`/invoices/${inv.id}/pdf`); }} className="inline-block px-2 py-1 hover:bg-zinc-100 rounded" data-testid={`pdf-invoice-${inv.id}`}><FileText size={14} /></button>
                    <button onClick={() => del(inv.id)} className="px-2 py-1 hover:bg-red-50 text-red-600 rounded"><Trash2 size={14} /></button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan="7" className="text-center text-zinc-400 py-10">{items.length === 0 ? "Sin facturas todavía." : "Sin resultados."}</td></tr>}
          </tbody>
        </table>
      </div>

      <PdfPreviewDialog path={pdfPath} filename={pdfName} onClose={() => setPdfPath(null)} />
    </div>
  );
}
